// Core packages
import { formatDate } from '@angular/common';
import { Injectable } from '@angular/core';

// Third party packages
import { TranslateService } from '@ngx-translate/core';
import { Observable } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

// Custom packages
import { ListApiResponseV2 } from 'src/app/shared/interfaces/_base/api-response.interface';
import { ApiService } from 'src/app/shared/services/api.service';
import { ConfigService } from 'src/app/shared/services/config.service';
import { FileService } from 'src/app/shared/services/file.service';
import { HelperService } from 'src/app/shared/services/helper.service';

/**
 * Row of the "Nota informativa" companies list
 * (@see GET companies/informative-note on the back-end)
 */
export interface InformativeNoteCompany {
  _id: string;
  name: string;
  email: string;
  pec: string;
  phone: string;
}

/** Mime type of the generated "Nota informativa" document */
export const INFORMATIVE_NOTE_PDF_MIME = 'application/pdf';

/**
 * "Nota informativa" service: admin companies that have the condo-owner privacy
 * access key (company.subscription.informativeNote). Independent additional service,
 * compatible with both "privacy" and "privacySag".
 *
 * @since 3.0.0
 */
@Injectable({
  providedIn: 'root'
})
export class InformativeNoteService {

  /**
   * Class constructor
   */
  constructor(
    private apiService: ApiService,
    private configService: ConfigService,
    private fileService: FileService,
    private helperService: HelperService,
    private translateService: TranslateService,
  ) { }

  /**
   * Generate the "Nota informativa" PDF (4 QR codes with clickable links) of the given company.
   * The front-end host is sent so the back-end can build the public QR links;
   * the back-end only accepts the platform's own front-ends.
   *
   * @since 3.0.0
   *
   * @param companyId Company Id
   */
  generateDocument(companyId: string): Observable<Blob> {
    return this.apiService.apiCall(
      'POST',
      `companies/${companyId}/informative-note/document`,
      { frontendOrigin: this.configService.frontEndHost },
      { responseType: 'blob' },
      undefined,
      undefined,
      'v2'
    ).pipe(
      catchError((err) => this.helperService.parseErrorBlob(err))
    );
  }

  /**
   * Generate the "Nota informativa" document as a ready-to-save file
   * (typed blob + localized, timestamped file name)
   *
   * @since 3.0.0
   *
   * @param companyId Company Id
   */
  generateDocumentFile(companyId: string): Observable<{ blob: Blob, fileName: string }> {
    const date = formatDate(new Date(), 'yyyyMMddHHmmss', 'en-EN');
    const fileName = `${this.translateService.instant('companies.detail.privacyDocsCard.informativeNote.exportFileName')}-${date}.pdf`;
    return this.generateDocument(companyId).pipe(
      map((blob: Blob) => ({ blob: new Blob([blob], { type: INFORMATIVE_NOTE_PDF_MIME }), fileName }))
    );
  }

  /**
   * Upload a generated document through the standard files endpoint, so it can be
   * pre-attached to SendEmailDialogComponent ("attachments" file-uploader value).
   *
   * @since 3.0.0
   *
   * @param file Generated document
   * @param fileName File name shown to the recipients
   * @returns Uploaded files, in the same shape returned to the file-uploader field
   */
  uploadForEmail(file: Blob, fileName: string): Observable<any[]> {
    const formData = new FormData();
    formData.append('file[]', file, fileName);
    return this.fileService.uploadImportingFiles(formData).pipe(
      map((res: any) => {
        if (!res || !res.status || !Array.isArray(res.files) || !res.files.length) {
          throw new Error((res && res.message) || 'Upload failed');
        }
        return res.files;
      })
    );
  }

  /**
   * Get the paginated list of "Nota informativa" companies
   *
   * @since 3.0.0
   */
  getList(
    start?: number,
    limit?: number,
    sort?: string,
    sortVersus?: string,
    search?: { [key: string]: string }
  ): Observable<ListApiResponseV2<InformativeNoteCompany>> {
    return this.apiService.apiCall('GET', 'companies/informative-note', this.buildParams(start, limit, sort, sortVersus, search), undefined, undefined, undefined, 'v2');
  }

  /**
   * Get the Excel export of the "Nota informativa" companies (same filters as the list)
   *
   * @since 3.0.0
   */
  getExport(sort?: string, sortVersus?: string, search?: { [key: string]: string }): Observable<Blob> {
    return this.apiService.apiCall(
      'GET',
      'companies/informative-note/export',
      this.buildParams(undefined, undefined, sort, sortVersus, search),
      { responseType: 'blob' },
      undefined,
      undefined,
      'v2'
    ).pipe(
      catchError((err) => this.helperService.parseErrorBlob(err))
    );
  }

  /**
   * Build query params, flattening search as search[key]=value
   */
  private buildParams(
    start?: number,
    limit?: number,
    sort?: string,
    sortVersus?: string,
    search?: { [key: string]: string }
  ): { [key: string]: any } {
    const params: { [key: string]: any } = {};
    if (typeof start === 'number') {
      params.start = start;
    }
    if (typeof limit === 'number') {
      params.limit = limit;
    }
    if (sort) {
      params.sort = sort;
    }
    if (sortVersus) {
      params.sortVersus = sortVersus;
    }
    if (search) {
      Object.keys(search).forEach(key => {
        if (search[key] !== null && search[key] !== undefined && search[key] !== '') {
          params[`search[${key}]`] = search[key];
        }
      });
    }
    return params;
  }
}
